// Global Instructions Rule Applied!
//
// Capital Call Payments: batch receipt entry. One row per live Closed
// commitment with Committed / Called / Paid / Outstanding and an editable
// Payment amount, so banking a call is one pass down a grid instead of one
// modal per LP.
//
// Fund-scoped by necessity, not by choice: a capital_contributions row has no
// capital_call_id, so which call a payment settles is the FIFO attribution in
// capital_calls__payment_status (oldest issued call first). Outstanding is
// therefore a per-LP, fund-level number. The per-call breakdown lives in the
// fund tab's Paid Status section.
//
// Reads cc__fund_position unchanged — it already drives off the Closed
// commitment pool, so an LP with no calls yet appears with its full commitment
// uncalled and can still be paid (a prepayment). Writes through
// cc__record_contributions_batch, which is all-or-nothing and writes nothing
// at all when it returns the duplicate (409) or row-validation (422) envelope.
//
// Two modes, like CallPositionView: embedded (fundId fixed, the host owns the
// section chrome) and standalone inside its own FormSection.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, InputNumber, Modal, Space, Table, Tag, Tooltip, Typography } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import type { TableProps } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useNumberDisplay, useNumberFormat } from '../../../core/utils/inputNumberFormat.ts'
import type { CapitalCallPositionRow } from '../../../state_manager/data_models/capital_call_position.ts'
import { fetchFundCallPosition, recordContributionsBatch } from '../services/capitalCallsService.ts'
import type { BatchPaymentRow } from '../services/capitalCallsService.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const WIRE_DATE = 'YYYY-MM-DD'
/** below this a figure is noise from numeric rounding, not a real balance */
const EPSILON = 0.005
/** Money is cents. currencyFmt now carries the input guard itself (cfg
 *  number_formats.currency.input_decimals), so a typed amount arrives already
 *  rounded. This stays for the arithmetic: a float sum of two clean amounts can
 *  still render a cent low (246128.8 + 126439.4 shows .19), and the submitted
 *  figure must be the one the operator saw. */
const toCents = (value: number): number => Math.round(value * 100) / 100

interface DuplicateEntry {
  row_index?: number
  fund_commitment_id?: string | null
  investor_name?: string | null
  amount?: number | null
  payment_date?: string | null
}

interface RowError {
  fund_commitment_id?: string | null
  investor_name?: string | null
  status_message?: string | null
}

export interface PaymentEntryGridProps {
  fundId: string | null
  /** called after a batch books, so a host tab can refetch its own sections */
  onRecorded?: () => void
  /** false when the host renders its own FormSection around the grid */
  withSection?: boolean
}

export default function PaymentEntryGrid({
  fundId,
  onRecorded,
  withSection = true
}: PaymentEntryGridProps) {
  const [modalApi, modalContextHolder] = Modal.useModal()
  const currencyFmt = useNumberFormat('currency')
  const currencyText = useNumberDisplay('currency')

  const [rows, setRows] = useState<CapitalCallPositionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [batchDate, setBatchDate] = useState<string>(dayjs().format(WIRE_DATE))
  const [amounts, setAmounts] = useState<Record<string, number | null>>({})
  const [dates, setDates] = useState<Record<string, string | null>>({})
  const [selected, setSelected] = useState<React.Key[]>([])
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    if (!fundId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundCallPosition(fundId, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to load the fund position')
        return
      }
      setRows(res.data.rows ?? [])
    } finally {
      setLoading(false)
    }
  }, [fundId])

  useEffect(() => {
    void load()
  }, [load])

  // ----- entry state -----
  const resetEntry = useCallback(() => {
    setAmounts({})
    setDates({})
    setRowErrors({})
  }, [])

  const outstandingOf = useCallback(
    (row: CapitalCallPositionRow) => {
      const value = row.prog__outstanding ?? 0
      return value > EPSILON ? value : null
    },
    []
  )

  /** the rows a bulk action acts on: the selection, or everything when empty */
  const bulkTargets = useMemo(() => {
    if (selected.length === 0) return rows
    const picked = new Set(selected.map(String))
    return rows.filter((r) => picked.has(r.fund_commitments__id))
  }, [rows, selected])

  const fillOutstanding = useCallback(() => {
    setAmounts((prev) => {
      const next = { ...prev }
      for (const row of bulkTargets) {
        const value = outstandingOf(row)
        if (value != null) next[row.fund_commitments__id] = value
      }
      return next
    })
  }, [bulkTargets, outstandingOf])

  const clearAmounts = useCallback(() => {
    setAmounts((prev) => {
      const next = { ...prev }
      for (const row of bulkTargets) delete next[row.fund_commitments__id]
      return next
    })
  }, [bulkTargets])

  /** what will actually be recorded: every row carrying an amount, ticked or
   *  not — the amount column is the single visible source of truth */
  const entered = useMemo(
    () =>
      rows
        .map((row, index) => ({ row, index, amount: amounts[row.fund_commitments__id] ?? null }))
        .filter((e) => e.amount != null && Math.abs(e.amount) > EPSILON),
    [rows, amounts]
  )

  const totals = useMemo(() => {
    const sum = (pick: (r: CapitalCallPositionRow) => number | null | undefined) =>
      rows.reduce((acc, r) => acc + (pick(r) ?? 0), 0)
    return {
      committed: toCents(sum((r) => r.fund_commitments__amount)),
      called: toCents(sum((r) => r.prog__called_to_date)),
      paid: toCents(sum((r) => r.prog__paid_amount)),
      outstanding: toCents(sum((r) => r.prog__outstanding)),
      entered: toCents(entered.reduce((acc, e) => acc + (e.amount ?? 0), 0))
    }
  }, [rows, entered])

  // ----- submit -----
  const submit = useCallback(
    async (confirmDuplicate: boolean) => {
      if (!fundId || entered.length === 0) return
      setSaving(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const payload = {
          fund_id: fundId,
          payment_date: batchDate,
          rows: entered.map<BatchPaymentRow>((e) => {
            const own = dates[e.row.fund_commitments__id]
            return {
              fund_commitment_id: e.row.fund_commitments__id,
              amount: toCents(e.amount as number),
              // only a genuine override travels; the muted cell is a display
              // of the batch fallback, not a stamped value
              ...(own ? { payment_date: own } : {})
            }
          }),
          confirm_duplicate: confirmDuplicate
        }
        const res = await recordContributionsBatch(payload, token)

        if (res.duplicateWarning) {
          const duplicates = (res.data?.duplicates ?? []) as DuplicateEntry[]
          modalApi.confirm({
            title: 'Possible duplicate payments',
            content: (
              <div>
                <p>{res.data?.status_message ?? 'Some payments match a contribution already recorded.'}</p>
                <p className='mb-1'>Nothing has been written yet.</p>
                <ul className='mb-2 pl-4'>
                  {duplicates.map((d, i) => (
                    <li key={`${d.fund_commitment_id ?? i}`}>
                      {d.investor_name ?? 'Investor'} — {currencyText(d.amount ?? 0)} on{' '}
                      {d.payment_date ? dayjs(d.payment_date).format(DATE_FORMAT) : '-'}
                    </li>
                  ))}
                </ul>
              </div>
            ),
            okText: 'Record anyway',
            onOk: async () => {
              await submit(true)
            }
          })
          return
        }

        if (res.validationError) {
          const errors = (res.data?.errors ?? []) as RowError[]
          const marks: Record<string, string> = {}
          for (const e of errors) {
            if (e.fund_commitment_id) marks[e.fund_commitment_id] = e.status_message ?? 'Rejected'
          }
          setRowErrors(marks)
          showErrorToast(res.error || 'Some rows could not be recorded')
          return
        }

        if (res.error) {
          showErrorToast(res.error)
          return
        }

        const count = (res.data?.recorded as number) ?? entered.length
        toast.success(`${count} payment${count === 1 ? '' : 's'} recorded and receipts booked`)
        resetEntry()
        setSelected([])
        await load()
        onRecorded?.()
      } finally {
        setSaving(false)
      }
    },
    [fundId, entered, batchDate, dates, modalApi, currencyText, resetEntry, load, onRecorded]
  )

  // ----- columns -----
  const columns = useMemo<TableProps<CapitalCallPositionRow>['columns']>(
    () => [
      {
        title: 'Investor',
        dataIndex: 'investors__name',
        key: 'investor',
        fixed: 'left' as const,
        width: 200,
        render: (value: string | null, row: CapitalCallPositionRow) => {
          const error = rowErrors[row.fund_commitments__id]
          return (
            <span>
              {value ?? '—'}
              {error ? (
                <Tooltip title={error}>
                  <Tag color='red' className='ml-2'>
                    rejected
                  </Tag>
                </Tooltip>
              ) : null}
            </span>
          )
        }
      },
      {
        title: 'Committed',
        dataIndex: 'fund_commitments__amount',
        key: 'committed',
        width: 130,
        align: 'right' as const,
        render: (v: number | null) => currencyText(v ?? 0)
      },
      {
        title: 'Called',
        dataIndex: 'prog__called_to_date',
        key: 'called',
        width: 130,
        align: 'right' as const,
        render: (v: number | null) =>
          Math.abs(v ?? 0) < EPSILON ? <Text type='secondary'>—</Text> : currencyText(v ?? 0)
      },
      {
        title: 'Paid',
        dataIndex: 'prog__paid_amount',
        key: 'paid',
        width: 130,
        align: 'right' as const,
        render: (v: number | null) =>
          Math.abs(v ?? 0) < EPSILON ? <Text type='secondary'>—</Text> : currencyText(v ?? 0)
      },
      {
        title: 'Outstanding',
        dataIndex: 'prog__outstanding',
        key: 'outstanding',
        width: 130,
        align: 'right' as const,
        render: (v: number | null) => {
          const value = v ?? 0
          if (Math.abs(value) < EPSILON) return <Text type='secondary'>—</Text>
          return <Text type='danger'>{currencyText(value)}</Text>
        }
      },
      {
        title: 'Payment',
        key: 'payment',
        width: 170,
        align: 'right' as const,
        render: (_: unknown, row: CapitalCallPositionRow) => {
          const id = row.fund_commitments__id
          const value = amounts[id] ?? null
          const outstanding = outstandingOf(row) ?? 0
          // a prepayment is legitimate — warn, never block
          const over = value != null && value - outstanding > EPSILON
          return (
            <InputNumber
              size='small'
              className='w-full'
              value={value}
              {...currencyFmt}
              placeholder='—'
              status={rowErrors[id] ? 'error' : over ? 'warning' : undefined}
              onChange={(v) =>
                setAmounts((prev) => ({ ...prev, [id]: v == null ? null : Number(v) }))
              }
            />
          )
        }
      },
      {
        title: 'Date',
        key: 'date',
        width: 140,
        render: (_: unknown, row: CapitalCallPositionRow) => {
          const id = row.fund_commitments__id
          const own = dates[id] ?? null
          // muted = inherited from the batch date, solid = this row's own
          return (
            <DatePicker
              size='small'
              variant='borderless'
              className={own ? 'w-full' : 'w-full opacity-60'}
              value={own ? dayjs(own) : batchDate ? dayjs(batchDate) : null}
              format={DATE_FORMAT}
              allowClear
              onChange={(d) =>
                setDates((prev) => ({ ...prev, [id]: d ? d.format(WIRE_DATE) : null }))
              }
            />
          )
        }
      }
    ],
    [amounts, dates, batchDate, currencyFmt, currencyText, outstandingOf, rowErrors]
  )

  const overpayments = useMemo(
    () =>
      entered
        .map((e) => ({
          name: e.row.investors__name ?? 'Investor',
          excess: toCents((e.amount ?? 0) - (outstandingOf(e.row) ?? 0))
        }))
        .filter((o) => o.excess > EPSILON),
    [entered, outstandingOf]
  )

  const headerActions = (
    <Space>
      <Button size='small' disabled={loading || rows.length === 0} onClick={fillOutstanding}>
        {selected.length > 0 ? `Fill outstanding (${selected.length})` : 'Fill outstanding (all)'}
      </Button>
      <Button size='small' disabled={loading || rows.length === 0} onClick={clearAmounts}>
        {selected.length > 0 ? `Clear amounts (${selected.length})` : 'Clear amounts (all)'}
      </Button>
      <Button size='small' icon={<ReloadOutlined />} disabled={loading} onClick={() => void load()}>
        Refresh
      </Button>
    </Space>
  )

  const titleSuffix =
    selected.length > 0 ? (
      <Space size='small'>
        <Tag color='blue'>{selected.length} selected</Tag>
        <Button type='link' size='small' className='p-0' onClick={() => setSelected([])}>
          Clear selection
        </Button>
      </Space>
    ) : null

  const body = (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between gap-3 flex-wrap'>
        <Space align='center'>
          <Text type='secondary'>Payment date</Text>
          <DatePicker
            value={batchDate ? dayjs(batchDate) : null}
            format={DATE_FORMAT}
            allowClear={false}
            onChange={(d) => setBatchDate(d ? d.format(WIRE_DATE) : dayjs().format(WIRE_DATE))}
          />
        </Space>
        <Text type='secondary'>
          {entered.length} of {rows.length} LPs entered · {currencyText(totals.entered)} of{' '}
          {currencyText(totals.outstanding)} outstanding
        </Text>
      </div>

      <Table<CapitalCallPositionRow>
        size='small'
        rowKey='fund_commitments__id'
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys)
        }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className='font-medium'>
              <Table.Summary.Cell index={0} />
              <Table.Summary.Cell index={1}>Total</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align='right'>
                {currencyText(totals.committed)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align='right'>
                {currencyText(totals.called)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align='right'>
                {currencyText(totals.paid)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align='right'>
                {currencyText(totals.outstanding)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align='right'>
                {currencyText(totals.entered)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />

      {overpayments.length > 0 ? (
        <Text type='warning'>
          {overpayments
            .map((o) => `${o.name} — ${currencyText(o.excess)} above outstanding`)
            .join(' · ')}
          {' '}will be recorded as prepayment.
        </Text>
      ) : null}

      <div className='flex items-center justify-between gap-3 flex-wrap'>
        <Text type='secondary'>
          Records every row carrying an amount, selected or not. Payments are applied to the oldest
          outstanding issued call first; each receipt books automatically.
        </Text>
        <Button
          type='primary'
          loading={saving}
          disabled={entered.length === 0}
          onClick={() => void submit(false)}
        >
          {entered.length === 0
            ? 'Record payments'
            : `Record ${entered.length} payment${entered.length === 1 ? '' : 's'}`}
        </Button>
      </div>
    </div>
  )

  if (!fundId) {
    return <Text type='secondary'>Save the fund first to record payments.</Text>
  }

  if (rows.length === 0 && !loading) {
    const empty = (
      <Text type='secondary'>
        No closed commitments in this fund yet — payments are recorded against Closed commitments.
      </Text>
    )
    return withSection ? (
      <FormSection title='Payments' titleColor='purple' scrollable={false}>
        {empty}
      </FormSection>
    ) : (
      empty
    )
  }

  return (
    <>
      {modalContextHolder}
      {withSection ? (
        <FormSection
          title='Payments'
          titleColor='purple'
          scrollable={false}
          titleSuffix={titleSuffix}
          headerActions={headerActions}
        >
          {body}
        </FormSection>
      ) : (
        <div className='flex flex-col gap-2'>
          <div className='flex items-center justify-between gap-2 flex-wrap'>
            <span>{titleSuffix}</span>
            {headerActions}
          </div>
          {body}
        </div>
      )}
    </>
  )
}
